import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button, Card, ErrorMessage, Field, Input, PasswordInput } from '../components/ui.tsx';
import { configApi } from '../lib/api.ts';
import { authClient } from '../lib/auth-client.ts';

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/entrar' });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Con el registro por invitación no se ofrece "crear cuenta": llevaría a un
  // formulario que el servidor va a rechazar. Mientras no se sabe, no se enseña.
  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get, staleTime: Infinity });

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const { error: signInError } = await authClient.signIn.email({ email, password });

    setLoading(false);

    if (signInError) {
      // Mensaje genérico a propósito: distinguir "no existe ese correo" de
      // "contraseña incorrecta" le confirma a un atacante qué cuentas existen.
      setError(t('auth.signIn.failed'));
      return;
    }

    await navigate({ to: search.redirect ?? '/' });
  }

  return (
    <AuthLayout>
      <Card title={t('auth.signIn.title')}>
        <form onSubmit={handleSubmit} noValidate>
          <ErrorMessage>{error}</ErrorMessage>

          <Field id="email" label={t('common.email')}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field id="password" label={t('common.password')}>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <p className="-mt-1 mb-5 text-right text-sm">
            <Link to="/recuperar" className="text-[var(--color-brand)] hover:underline">
              {t('auth.signIn.forgot')}
            </Link>
          </p>

          <Button type="submit" loading={loading}>
            {loading ? t('common.loading') : t('auth.signIn.submit')}
          </Button>
        </form>

        {config?.registrationMode === 'open' ? (
          <p className="mt-5 text-center text-sm text-[var(--color-muted)]">
            {t('auth.signIn.noAccount')}{' '}
            <Link to="/registro" className="text-[var(--color-brand)] underline">
              {t('auth.signIn.createOne')}
            </Link>
          </p>
        ) : null}

        {config?.registrationMode === 'invitation' ? (
          <p className="mt-5 text-center text-sm leading-relaxed text-[var(--color-muted)]">
            {t('auth.signIn.invitationOnly')}
          </p>
        ) : null}
      </Card>
    </AuthLayout>
  );
}
